import React, { useState } from 'react';
import { z } from 'zod';
import KubitModal from '../KubitModal';
import { ModalActions } from '../KubitModal/KubitModalUI';
import { PrimaryButton, SecondaryButton } from '../Button';
import { MaybeNil } from '../../utils/interfaces';
import { editInfoModalLookup, IInfoModalEntity } from './utils';
import { DescriptionField, NameField } from '../Form';
import {
  descriptionFieldSchema,
  isDuplicate,
  nameFieldSchema,
} from '../../utils/validation';
import { formErrorViewed } from '../../utils/analytics/events/error/events';
import { useAnalytics } from '../../utils/analytics/useAnalytics';

interface IProps {
  entity: IInfoModalEntity;
  name: string;
  description: string;
  onSubmit: (newName: string, newDescription: string) => Promise<void> | void;
  onClose: () => void;
  entitiesList: Array<{ name: MaybeNil<string> }>;
  snapshotName?: string;
  submitLabel?: string;
  isLoading?: boolean;
  trackContext?: string;
  trackSubmitAction?: string;
  trackCancelAction?: string;
}

const validationSchema = (nameMaxLimit: number, isNameDuplicate?: boolean) =>
  z.object({
    draftName: nameFieldSchema(nameMaxLimit, isNameDuplicate),
    draftDescription: descriptionFieldSchema,
  });

export default ({
  entity,
  name,
  description,
  onSubmit,
  onClose,
  entitiesList,
  snapshotName,
  submitLabel,
  isLoading,
  trackContext,
  trackSubmitAction,
  trackCancelAction,
}: IProps) => {
  const { track } = useAnalytics();
  const [draftName, setDraftName] = useState(name);
  const [draftDescription, setDraftDescription] = useState(description);
  const [submitting, setSubmitting] = useState(false);

  const config = editInfoModalLookup[entity];

  const nameLimit = config.nameLimit;

  const isDuplicateName = isDuplicate({
    name: draftName,
    entitiesList,
    snapshotName,
  });

  const safeParse = validationSchema(nameLimit, isDuplicateName).safeParse({
    draftName,
    draftDescription,
  });

  const isFormValid = safeParse.success;

  const errors = !isFormValid ? safeParse?.error?.format() : null;

  const handleSubmit = () => {
    setSubmitting(true);

    if (isFormValid) {
      onSubmit(safeParse.data.draftName, safeParse.data.draftDescription || '');
    } else {
      track(formErrorViewed(config.analyticsErrorProducer(errors)));
    }
  };

  return (
    <KubitModal header={config.header} onClose={onClose} open>
      <NameField
        label={config.nameLabel}
        value={draftName}
        targetErrorElement={config.targetElement}
        maxLength={nameLimit}
        onChange={setDraftName}
        isDuplicate={submitting && isDuplicateName}
        hasError={submitting && Boolean(errors?.draftName)}
      />
      <DescriptionField
        label={config.descriptionLabel}
        value={draftDescription}
        targetErrorElement={config.targetElement}
        onChange={setDraftDescription}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onClose}
          disabled={isLoading}
          trackContext={trackContext}
          trackAction={trackCancelAction}
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          isGreyedOut={!isFormValid}
          disabled={isLoading}
          trackContext={trackContext}
          trackAction={trackSubmitAction}
        >
          {submitLabel || 'Update'}
        </PrimaryButton>
      </ModalActions>
    </KubitModal>
  );
};
